import { valueLabel } from "../shared/value-copy";
import { PreOrderCustomerPicker } from "./PreOrderCustomerPicker";
import { valueError } from "../shared/value-copy";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  Grid,
  Inline,
  SelectField,
  Stack,
  StatusBadge,
  TextAreaField
} from "@leylion/design-system";
import {
  cancelPreOrder,
  capturePreOrderBalance,
  capturePreOrderDeposit,
  confirmPreOrder,
  fulfillPreOrder,
  generateBookingReceipt,
  getPreOrder,
  releasePreOrderToKitchen,
  reschedulePreOrder,
  searchPreOrders,
  updatePreOrder
} from "../api/client";
import type {
  BookingReceipt,
  Customer,
  PaymentMethod,
  PreOrderCancellationDisposition,
  PreOrderDetail,
  PreOrderKitchenReleaseState,
  PreOrderSummary,
  Receipt,
  ScheduledOrderStatus
} from "../api/cashier-types";
import { formatMoneyMinor, majorInputToMinor, minorToMajorInput } from "./cashier-money";
import { BRAND } from "../brand";

interface FutureOrdersDrawerProps {
  open: boolean;
  onClose: () => void;
  language: "ar" | "en";
  permissions: string[];
  onOpenPreOrder: (detail: PreOrderDetail) => void;
  onReceipt?: (receipt: Receipt) => void;
  onChanged?: () => void;
}

type FutureOrderRange =
  | "Today"
  | "Tomorrow"
  | "SevenDays"
  | "UpcomingWindow"
  | "PastSevenDays"
  | "PastThirtyDays"
  | "PastAll";

function rangeQuery(range: FutureOrderRange): { from?: string; to?: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (range === "UpcomingWindow") {
    return { from: today.toISOString() };
  }
  if (range === "PastAll") {
    return { from: new Date(0).toISOString(), to: today.toISOString() };
  }

  const start = new Date(today);
  const end = new Date(today);
  switch (range) {
    case "Tomorrow":
      start.setDate(start.getDate() + 1);
      end.setDate(end.getDate() + 2);
      break;
    case "SevenDays":
      end.setDate(end.getDate() + 7);
      break;
    case "PastSevenDays":
      start.setDate(start.getDate() - 7);
      break;
    case "PastThirtyDays":
      start.setDate(start.getDate() - 30);
      break;
    case "Today":
    default:
      end.setDate(end.getDate() + 1);
      break;
  }

  return { from: start.toISOString(), to: end.toISOString() };
}

const statusOptions: Array<ScheduledOrderStatus | ""> = [
  "",
  "Draft",
  "AwaitingDeposit",
  "Confirmed",
  "Cancelled",
  "Fulfilled"
];

function escapeHtml(value: string | null | undefined): string {
  return (value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function toLocalInput(value: string): string {
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function statusTone(status: ScheduledOrderStatus): "neutral" | "info" | "warning" | "success" | "danger" {
  switch (status) {
    case "AwaitingDeposit":
      return "warning";
    case "Confirmed":
      return "success";
    case "Cancelled":
      return "danger";
    case "Fulfilled":
      return "info";
    default:
      return "neutral";
  }
}

function releaseTone(state: PreOrderKitchenReleaseState): "neutral" | "info" | "warning" | "success" | "danger" {
  if (state === "Released") return "success";
  if (state === "Failed") return "danger";
  return "neutral";
}

function releaseLabel(state: PreOrderKitchenReleaseState, ar: boolean): string {
  if (state === "Released") return ar ? "أُرسل للتحضير" : "Released for preparation";
  if (state === "Failed") return ar ? "فشل الإرسال" : "Release failed";
  return ar ? "بانتظار نافذة التحضير" : "Awaiting preparation window";
}

function scheduledStatusLabel(status: ScheduledOrderStatus, ar: boolean): string {
  const labels: Record<ScheduledOrderStatus, { ar: string; en: string }> = {
    Draft: { ar: "مسودة", en: "Draft" },
    AwaitingDeposit: { ar: "بانتظار العربون", en: "Awaiting deposit" },
    Confirmed: { ar: "مؤكد", en: "Confirmed" },
    Cancelled: { ar: "ملغي", en: "Cancelled" },
    Fulfilled: { ar: "مكتمل", en: "Fulfilled" }
  };
  return labels[status][ar ? "ar" : "en"];
}

function serviceTypeLabel(serviceType: PreOrderSummary["serviceType"], ar: boolean): string {
  const labels = {
    WalkIn: { ar: "استلام", en: "Pickup" },
    DineIn: { ar: "داخلي", en: "Dine-in" },
    Takeaway: { ar: "سفري", en: "Takeaway" },
    Delivery: { ar: "توصيل", en: "Delivery" }
  } as const;
  return labels[serviceType][ar ? "ar" : "en"];
}

function orderStatusLabel(status: PreOrderSummary["orderStatus"], ar: boolean): string {
  const labels = {
    Draft: { ar: "مسودة", en: "Draft" },
    Held: { ar: "معلق", en: "Held" },
    SentToKitchen: { ar: "قيد التحضير", en: "Preparing" },
    Ready: { ar: "جاهز", en: "Ready" },
    Completed: { ar: "مكتمل", en: "Completed" },
    Cancelled: { ar: "ملغي", en: "Cancelled" },
    Refunded: { ar: "مسترجع", en: "Refunded" }
  } as const;
  return labels[status][ar ? "ar" : "en"];
}

function formatCountdown(target: string, now: number, ar: boolean): string {
  const targetMs = new Date(target).getTime();
  if (!Number.isFinite(targetMs)) return "—";
  const diff = targetMs - now;
  const absoluteSeconds = Math.max(0, Math.floor(Math.abs(diff) / 1000));
  const days = Math.floor(absoluteSeconds / 86_400);
  const hours = Math.floor((absoluteSeconds % 86_400) / 3_600);
  const minutes = Math.floor((absoluteSeconds % 3_600) / 60);
  const seconds = absoluteSeconds % 60;
  const value = days > 0
    ? `${days}${ar ? "ي" : "d"} ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
    : `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  if (diff < 0) return ar ? `متأخر ${value}` : `Overdue ${value}`;
  return ar ? `متبقٍ ${value}` : `${value} remaining`;
}

function isTerminalPreOrder(item: PreOrderSummary): boolean {
  return item.status === "Fulfilled"
    || item.status === "Cancelled"
    || item.orderStatus === "Completed"
    || item.orderStatus === "Cancelled"
    || item.orderStatus === "Refunded"
    || item.fulfillmentStatus === "Completed"
    || item.fulfillmentStatus === "Cancelled";
}

function terminalScheduleLabel(item: PreOrderSummary, ar: boolean): string {
  const cancelled = item.status === "Cancelled"
    || item.orderStatus === "Cancelled"
    || item.fulfillmentStatus === "Cancelled";
  if (cancelled) return ar ? "ملغى — أوقف العداد" : "Cancelled — timer stopped";
  const fulfilledAt = item.fulfilledAt
    ? new Date(item.fulfilledAt).toLocaleString(ar ? "ar-AE" : "en-AE")
    : null;
  return fulfilledAt
    ? ar ? `تم التسليم ${fulfilledAt}` : `Fulfilled ${fulfilledAt}`
    : ar ? "تم التسليم — أوقف العداد" : "Fulfilled — timer stopped";
}

export function FutureOrdersDrawer({
  open,
  onClose,
  language,
  permissions,
  onOpenPreOrder,
  onReceipt,
  onChanged
}: FutureOrdersDrawerProps) {
  const ar = language === "ar";
  const can = (permission: string) => permissions.includes(permission);
  const [items, setItems] = useState<PreOrderSummary[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ScheduledOrderStatus | "">("");
  const [range, setRange] = useState<FutureOrderRange>("SevenDays");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<PreOrderDetail | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [fulfillOpen, setFulfillOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [clockNow, setClockNow] = useState(() => Date.now());

  const [fulfillmentAt, setFulfillmentAt] = useState("");
  const [preparationLeadMinutes, setPreparationLeadMinutes] = useState("60");
  const [depositRequired, setDepositRequired] = useState("");
  const [notes, setNotes] = useState("");
  const [contactName, setContactName] = useState("");
  const [linkedCustomer, setLinkedCustomer] = useState<Customer | null>(null);
  const [customerBusy, setCustomerBusy] = useState(false);
  const [contactPhone, setContactPhone] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [addressNotes, setAddressNotes] = useState("");
  const [editorDetailsOnly, setEditorDetailsOnly] = useState(false);

  const [depositMethod, setDepositMethod] = useState<PaymentMethod>("Cash");
  const [depositAmount, setDepositAmount] = useState("");
  const [depositTendered, setDepositTendered] = useState("");
  const [depositReference, setDepositReference] = useState("");

  const [balanceMethod, setBalanceMethod] = useState<PaymentMethod>("Cash");
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceTendered, setBalanceTendered] = useState("");
  const [balanceReference, setBalanceReference] = useState("");
  const [handoverNote, setHandoverNote] = useState("");

  const [rescheduleFulfillmentAt, setRescheduleFulfillmentAt] = useState("");
  const [rescheduleLeadMinutes, setRescheduleLeadMinutes] = useState("60");
  const [rescheduleReason, setRescheduleReason] = useState("");

  const [cancellationReason, setCancellationReason] = useState("");
  const [cancellationDisposition, setCancellationDisposition] =
    useState<PreOrderCancellationDisposition>("RefundAll");
  const [partialDepositRefund, setPartialDepositRefund] = useState("");

  const formatMoney = (minor: number, currency = "AED") =>
    formatMoneyMinor(minor, currency, ar ? "ar-AE" : "en-AE");

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setError("");
    try {
      setItems(await searchPreOrders({ search, status, ...rangeQuery(range) }));
    } catch (caught) {
      if (!silent) {
        setError(caught instanceof Error ? caught.message : "Failed to load future orders.");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    void load();
    const polling = window.setInterval(() => void load(true), 10_000);
    return () => window.clearInterval(polling);
  }, [open, status, range]);

  const hasLiveCountdowns = useMemo(
    () => items.some((item) => !isTerminalPreOrder(item)),
    [items]
  );

  useEffect(() => {
    if (!open || !hasLiveCountdowns) return undefined;
    setClockNow(Date.now());
    const timer = window.setInterval(() => setClockNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [open, hasLiveCountdowns]);

  const openDetail = async (orderId: string) => {
    setLoading(true);
    setError("");
    try {
      const value = await getPreOrder(orderId);
      setDetail(value);
      onOpenPreOrder(value);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to open future order.");
    } finally {
      setLoading(false);
    }
  };

  const hydrateEditor = (value: PreOrderDetail) => {
    setDetail(value);
    setLinkedCustomer(value.workflow.customer ?? null);
    setFulfillmentAt(toLocalInput(value.summary.fulfillmentAt));
    setPreparationLeadMinutes(String(value.summary.preparationLeadMinutes));
    setDepositRequired(minorToMajorInput(value.summary.depositRequiredMinor, value.summary.currency));
    setNotes(value.summary.notes ?? "");
    setContactName(value.summary.contactName ?? value.summary.customerName ?? "");
    setContactPhone(value.summary.contactPhone ?? "");
    setVehicleNumber(value.summary.vehicleNumber ?? "");
    setAddressNotes(value.summary.addressNotes ?? "");
    setEditorDetailsOnly(
      !["Draft", "AwaitingDeposit"].includes(value.summary.status)
      || value.summary.kitchenReleaseState === "Released"
      || value.summary.depositPaidMinor > 0
    );
    setEditorOpen(true);
  };

  const save = async () => {
    if (!detail || customerBusy) return;
    setLoading(true);
    setError("");
    try {
      const depositRequiredMinor = depositRequired.trim()
        ? majorInputToMinor(depositRequired, detail.summary.currency)
        : 0;
      if (depositRequiredMinor == null || depositRequiredMinor < 0) {
        throw new Error(ar ? "أدخل مبلغ عربون اختيارياً صالحاً أو اتركه صفراً." : "Enter a valid optional deposit target or leave it at zero.");
      }
      const updated = await updatePreOrder(detail.summary.orderId, {
        ...(can("customers.read") ? { customerSelection: { customerId: linkedCustomer?.id ?? null } } : {}),
        fulfillmentAt: new Date(fulfillmentAt).toISOString(),
        preparationLeadMinutes: Number(preparationLeadMinutes),
        depositRequiredMinor,
        notes: notes.trim() || null,
        contactName: contactName.trim() || null,
        contactPhone: contactPhone.trim() || null,
        vehicleNumber: vehicleNumber.trim() || null,
        addressNotes: addressNotes.trim() || null,
        expectedOrderVersion: detail.summary.orderVersion,
        expectedScheduledOrderVersion: detail.summary.scheduledOrderVersion
      });
      setDetail(updated);
      setEditorOpen(false);
      await load();
      onChanged?.();
    } catch (caught) {
      setError(valueError(caught, language));
    } finally {
      setLoading(false);
    }
  };

  const confirm = async (item: PreOrderSummary) => {
    setLoading(true);
    setError("");
    try {
      const updated = await confirmPreOrder(
        item.orderId,
        item.orderVersion,
        item.scheduledOrderVersion
      );
      setDetail(updated);
      await load();
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to confirm future order.");
    } finally {
      setLoading(false);
    }
  };

  const openDeposit = async (item: PreOrderSummary) => {
    setLoading(true);
    setError("");
    try {
      const value = await getPreOrder(item.orderId);
      setDetail(value);
      const suggested = Math.min(
        value.summary.remainingBalanceMinor,
        Math.max(0, value.summary.depositRequiredMinor - value.summary.depositPaidMinor)
      );
      const amount = suggested > 0
        ? minorToMajorInput(suggested, value.summary.currency)
        : "";
      setDepositAmount(amount);
      setDepositTendered(amount);
      setDepositMethod("Cash");
      setDepositReference("");
      setDepositOpen(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to open deposit workflow.");
    } finally {
      setLoading(false);
    }
  };

  const collectDeposit = async () => {
    if (!detail) return;
    setLoading(true);
    setError("");
    try {
      const amountMinor = majorInputToMinor(depositAmount, detail.summary.currency);
      const tenderedMinor = depositMethod === "Cash"
        ? majorInputToMinor(depositTendered, detail.summary.currency)
        : amountMinor;
      if (amountMinor == null || tenderedMinor == null) {
        throw new Error(ar ? "أدخل مبلغ عربون صحيحاً." : "Enter a valid deposit amount.");
      }
      const result = await capturePreOrderDeposit(detail.summary.orderId, {
        method: depositMethod,
        amountMinor,
        tenderedMinor,
        provider: depositMethod === "Card" ? "POS Terminal" : null,
        providerReference: depositReference.trim() || null,
        clientOperationId: crypto.randomUUID(),
        expectedOrderVersion: detail.summary.orderVersion,
        expectedScheduledOrderVersion: detail.summary.scheduledOrderVersion
      });
      setDetail(result.preOrder);
      setDepositOpen(false);
      await load();
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to collect deposit.");
    } finally {
      setLoading(false);
    }
  };

  const releaseNow = async (item: PreOrderSummary) => {
    setLoading(true);
    setError("");
    try {
      const updated = await releasePreOrderToKitchen(
        item.orderId,
        item.orderVersion,
        item.scheduledOrderVersion,
        item.kitchenReleaseState === "Failed"
          ? "Manual retry after an automatic preparation release failure."
          : "Manually released before the automatic preparation horizon."
      );
      setDetail(updated);
      await load();
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to release the future order for preparation.");
    } finally {
      setLoading(false);
    }
  };

  const openBalance = async (item: PreOrderSummary) => {
    setLoading(true);
    setError("");
    try {
      const value = await getPreOrder(item.orderId);
      setDetail(value);
      const amount = minorToMajorInput(value.summary.remainingBalanceMinor, value.summary.currency);
      setBalanceAmount(amount);
      setBalanceTendered(amount);
      setBalanceMethod("Cash");
      setBalanceReference("");
      setBalanceOpen(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to open balance workflow.");
    } finally {
      setLoading(false);
    }
  };

  const collectBalance = async () => {
    if (!detail) return;
    setLoading(true);
    setError("");
    try {
      const amountMinor = majorInputToMinor(balanceAmount, detail.summary.currency);
      const tenderedMinor = balanceMethod === "Cash"
        ? majorInputToMinor(balanceTendered, detail.summary.currency)
        : amountMinor;
      if (amountMinor == null || tenderedMinor == null) {
        throw new Error(ar ? "أدخل مبلغ رصيد صحيحاً." : "Enter a valid balance amount.");
      }
      const result = await capturePreOrderBalance(detail.summary.orderId, {
        method: balanceMethod,
        amountMinor,
        tenderedMinor,
        provider: balanceMethod === "Card" ? "POS Terminal" : null,
        providerReference: balanceReference.trim() || null,
        clientOperationId: crypto.randomUUID(),
        expectedOrderVersion: detail.summary.orderVersion,
        expectedScheduledOrderVersion: detail.summary.scheduledOrderVersion
      });
      setDetail(result.preOrder);
      setBalanceOpen(false);
      await load();
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to collect the remaining balance.");
    } finally {
      setLoading(false);
    }
  };

  const openFulfillment = async (item: PreOrderSummary) => {
    setLoading(true);
    setError("");
    try {
      const value = await getPreOrder(item.orderId);
      setDetail(value);
      setHandoverNote("");
      setFulfillOpen(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to open fulfillment workflow.");
    } finally {
      setLoading(false);
    }
  };

  const completeFulfillment = async () => {
    if (!detail) return;
    setLoading(true);
    setError("");
    try {
      const result = await fulfillPreOrder(
        detail.summary.orderId,
        detail.summary.orderVersion,
        detail.summary.scheduledOrderVersion,
        handoverNote.trim() || null
      );
      setDetail(result.preOrder);
      setFulfillOpen(false);
      onReceipt?.(result.receipt);
      await load();
      onChanged?.();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to complete future-order handover.");
    } finally {
      setLoading(false);
    }
  };

  const openReschedule = async (item: PreOrderSummary) => {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const value = await getPreOrder(item.orderId);
      setDetail(value);
      setRescheduleFulfillmentAt(toLocalInput(value.summary.fulfillmentAt));
      setRescheduleLeadMinutes(String(value.summary.preparationLeadMinutes));
      setRescheduleReason("");
      setRescheduleOpen(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to open reschedule workflow.");
    } finally {
      setLoading(false);
    }
  };

  const submitReschedule = async () => {
    if (!detail) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const result = await reschedulePreOrder(detail.summary.orderId, {
        fulfillmentAt: new Date(rescheduleFulfillmentAt).toISOString(),
        preparationLeadMinutes: Number(rescheduleLeadMinutes),
        reason: rescheduleReason.trim(),
        expectedOrderVersion: detail.summary.orderVersion,
        expectedScheduledOrderVersion: detail.summary.scheduledOrderVersion
      });
      setDetail(result.preOrder);
      setRescheduleOpen(false);
      setNotice(result.approvalRequired
        ? ar
          ? `تم إرسال طلب إعادة الجدولة للمدير (${result.approvalRequestId ?? "—"}). لن يتغير الموعد حتى الموافقة والتنفيذ.`
          : `Reschedule approval was submitted (${result.approvalRequestId ?? "—"}). The schedule remains unchanged until approval and execution.`
        : ar
          ? "تمت إعادة جدولة الطلب بنجاح."
          : "The future order was rescheduled successfully.");
      await load();
      onChanged?.();
    } catch (caught) {
      setError(valueError(caught, language));
    } finally {
      setLoading(false);
    }
  };

  const openCancellation = async (item: PreOrderSummary) => {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const value = await getPreOrder(item.orderId);
      setDetail(value);
      setCancellationReason("");
      setCancellationDisposition(
        value.workflow.order.amountPaidMinor > 0 ? "RefundAll" : "NoPayment"
      );
      setPartialDepositRefund("");
      setCancelOpen(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Failed to open cancellation workflow.");
    } finally {
      setLoading(false);
    }
  };

  const submitCancellation = async () => {
    if (!detail) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const partialMinor = cancellationDisposition === "PartialRefund"
        ? majorInputToMinor(partialDepositRefund, detail.summary.currency)
        : null;
      if (cancellationDisposition === "PartialRefund" && partialMinor == null) {
        throw new Error(ar ? "أدخل مبلغ استرداد جزئي صالحاً." : "Enter a valid partial deposit refund.");
      }
      const result = await cancelPreOrder(detail.summary.orderId, {
        reason: cancellationReason.trim(),
        disposition: cancellationDisposition,
        depositRefundMinor: partialMinor,
        expectedOrderVersion: detail.summary.orderVersion,
        expectedScheduledOrderVersion: detail.summary.scheduledOrderVersion
      });
      setDetail(result.preOrder);
      setCancelOpen(false);
      setNotice(result.approvalRequired
        ? ar
          ? `تم إرسال طلب الإلغاء وتسوية العربون للمدير (${result.approvalRequestId ?? "—"}). لن يُلغى الطلب قبل الموافقة والتنفيذ.`
          : `Cancellation and deposit-settlement approval was submitted (${result.approvalRequestId ?? "—"}). The order remains active until approval and execution.`
        : ar
          ? "تم إلغاء الطلب المستقبلي بنجاح."
          : "The future order was cancelled successfully.");
      await load();
      onChanged?.();
    } catch (caught) {
      setError(valueError(caught, language));
    } finally {
      setLoading(false);
    }
  };

  const printBooking = async (item: PreOrderSummary) => {
    const popup = window.open("", "_blank", "width=420,height=760");
    setLoading(true);
    setError("");
    try {
      const receipt = await generateBookingReceipt(
        item.orderId,
        item.orderVersion,
        item.scheduledOrderVersion
      );
      if (!popup) throw new Error(ar ? "المتصفح منع نافذة الطباعة." : "The browser blocked the print window.");
      popup.document.write(renderBookingReceipt(receipt, ar));
      popup.document.close();
      popup.focus();
      popup.print();
      await load();
      onChanged?.();
    } catch (caught) {
      popup?.close();
      setError(caught instanceof Error ? caught.message : "Failed to print booking confirmation.");
    } finally {
      setLoading(false);
    }
  };

  const activeItems = useMemo(
    () => items.filter((item) => item.status !== "Cancelled" && item.status !== "Fulfilled"),
    [items]
  );
  const displayItems = useMemo(
    () => [...items].sort((left, right) => {
      const terminalDifference = Number(isTerminalPreOrder(left)) - Number(isTerminalPreOrder(right));
      if (terminalDifference !== 0) return terminalDifference;
      return new Date(left.fulfillmentAt).getTime() - new Date(right.fulfillmentAt).getTime();
    }),
    [items]
  );

  return (
    <>
      <Drawer
        className="future-orders-drawer"
        open={open}
        onClose={onClose}
        title={ar ? "الطلبات المستقبلية والحجوزات" : "Future Orders & Bookings"}
      >
        <Stack>
          {error ? (
            <Alert tone="danger" title={ar ? "تعذر تنفيذ العملية" : "Operation failed"} onDismiss={() => setError("")}>
              {error}
            </Alert>
          ) : null}
          {notice ? (
            <Alert tone="success" title={ar ? "تم تسجيل الإجراء" : "Action recorded"} onDismiss={() => setNotice("")}>
              {notice}
            </Alert>
          ) : null}
          <Alert tone="info" title={ar ? "الجدولة والإرسال التلقائي" : "Scheduling & automatic kitchen release"}>
            {ar
              ? "بعد حفظ الطلب وتأكيده، يرسله Leylion تلقائياً للمطبخ عند موعد الإرسال للتحضير. زر الإرسال اليدوي مخصص للتقديم المبكر أو لإعادة المحاولة عند فشل الإرسال."
              : "After the booking is saved and confirmed, Leylion releases it to the kitchen automatically at the preparation time. Manual send is only for early release or retry after failure."}
          </Alert>
          <Grid columns={3}>
            <Field
              label={ar ? "بحث بالحجز أو الطلب أو العميل" : "Search booking, order or customer"}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && void load()}
            />
            <SelectField
              data-testid="future-orders-date-range"
              label={ar ? "الفترة" : "Date range"}
              value={range}
              onChange={(event) => setRange(event.target.value as FutureOrderRange)}
            >
              <option value="Today">{ar ? "اليوم" : "Today"}</option>
              <option value="Tomorrow">{ar ? "غداً" : "Tomorrow"}</option>
              <option value="SevenDays">{ar ? "الأيام السبعة القادمة" : "Next 7 days"}</option>
              <option value="UpcomingWindow">{ar ? "كل الفترة القادمة" : "All upcoming"}</option>
              <option value="PastSevenDays">{ar ? "الأيام السبعة السابقة" : "Previous 7 days"}</option>
              <option value="PastThirtyDays">{ar ? "آخر 30 يوماً" : "Previous 30 days"}</option>
              <option value="PastAll">{ar ? "كل الفترة السابقة" : "All previous"}</option>
            </SelectField>
            <SelectField
              label={ar ? "الحالة" : "Status"}
              value={status}
              onChange={(event) => setStatus(event.target.value as ScheduledOrderStatus | "")}
            >
              {statusOptions.map((option) => (
                <option key={option || "all"} value={option}>
                  {option || (ar ? "جميع الحالات" : "All statuses")}
                </option>
              ))}
            </SelectField>
          </Grid>
          <Inline>
            <Button variant="secondary" onClick={() => void load()} disabled={loading}>
              {ar ? "تحديث" : "Refresh"}
            </Button>
            <StatusBadge tone="info">
              {ar ? `${activeItems.length} حجز نشط` : `${activeItems.length} active bookings`}
            </StatusBadge>
          </Inline>

          {items.length ? (
            <div className="future-orders-grid" data-testid="future-orders-list">
              {displayItems.map((item) => {
                const ready = item.orderStatus === "Ready" && item.fulfillmentStatus === "Ready";
                const released = item.kitchenReleaseState === "Released";
                const terminal = isTerminalPreOrder(item);
                const active = !terminal;
                const overdue = active && new Date(item.fulfillmentAt).getTime() < clockNow;
                return (
                  <Card
                    key={item.scheduledOrderId}
                    className={`future-order-card${released ? " is-kitchen-released" : ""}${item.kitchenReleaseState === "Failed" ? " is-release-failed" : ""}${terminal ? " is-terminal" : ""}`}
                    title={`${item.bookingNumber} · #${item.orderNumber}`}
                    description={new Date(item.fulfillmentAt).toLocaleString(ar ? "ar-AE" : "en-AE")}
                  >
                    <Stack>
                      <Inline>
                        <StatusBadge tone={statusTone(item.status)}>{scheduledStatusLabel(item.status, ar)}</StatusBadge>
                        <StatusBadge tone={releaseTone(item.kitchenReleaseState)}>
                          {releaseLabel(item.kitchenReleaseState, ar)}
                        </StatusBadge>
                        {released ? (
                          <StatusBadge tone={ready ? "success" : "info"}>{orderStatusLabel(item.orderStatus, ar)}</StatusBadge>
                        ) : null}
                        {active && !released && item.kitchenReleaseState !== "Failed" ? (
                          <StatusBadge tone="info">{ar ? "إرسال تلقائي" : "Auto release"}</StatusBadge>
                        ) : null}
                        <StatusBadge tone="neutral">{serviceTypeLabel(item.serviceType, ar)}</StatusBadge>
                        {item.rescheduleCount > 0 ? (
                          <StatusBadge tone="warning">
                            {ar ? `أعيدت الجدولة ${item.rescheduleCount}` : `Rescheduled ${item.rescheduleCount}×`}
                          </StatusBadge>
                        ) : null}
                        {item.cancellationDisposition ? (
                          <StatusBadge tone="danger">{item.cancellationDisposition}</StatusBadge>
                        ) : null}
                      </Inline>

                      <div className="future-order-schedule-grid">
                        <span>
                          <small>{ar ? "موعد الاستلام" : "Fulfillment"}</small>
                          <strong>{new Date(item.fulfillmentAt).toLocaleString(ar ? "ar-AE" : "en-AE")}</strong>
                          {terminal ? (
                            <em className="is-terminal" data-testid={`future-order-terminal-timer-${item.orderId}`}>
                              {terminalScheduleLabel(item, ar)}
                            </em>
                          ) : (
                            <em className={overdue ? "is-overdue" : ""}>
                              {formatCountdown(item.fulfillmentAt, clockNow, ar)}
                            </em>
                          )}
                        </span>
                        <span>
                          <small>{ar ? "موعد الإرسال للتحضير" : "Preparation release"}</small>
                          <strong>{new Date(item.kitchenReleaseAt).toLocaleString(ar ? "ar-AE" : "en-AE")}</strong>
                          {active && !released ? <em>{formatCountdown(item.kitchenReleaseAt, clockNow, ar)}</em> : null}
                        </span>
                      </div>

                      <strong className="future-order-customer">
                        {item.customerName ?? item.contactName ?? (ar ? "عميل غير مسمى" : "Unnamed customer")}
                      </strong>
                      <small>
                        {[item.contactPhone, item.vehicleNumber].filter(Boolean).join(" · ") || "—"}
                      </small>

                      {item.customerAddress || item.addressNotes ? (
                        <div className="future-order-location" data-testid={`future-order-location-${item.orderId}`}>
                          <small>{ar ? "العنوان / الموقع" : "Address / location"}</small>
                          {item.customerAddress ? <strong>{item.customerAddress}</strong> : null}
                          {item.addressNotes ? <span>{item.addressNotes}</span> : null}
                        </div>
                      ) : null}

                      <div className="future-order-money-grid">
                        <span><small>{ar ? "الإجمالي" : "Total"}</small><strong>{formatMoney(item.orderTotalMinor, item.currency)}</strong></span>
                        <span><small>{ar ? "العربون" : "Deposit"}</small><strong>{formatMoney(item.depositPaidMinor, item.currency)}</strong></span>
                        <span><small>{ar ? "المتبقي" : "Balance"}</small><strong>{formatMoney(item.remainingBalanceMinor, item.currency)}</strong></span>
                      </div>

                      {item.depositRefundedMinor > 0 || item.balanceRefundedMinor > 0 || item.depositForfeitedMinor > 0 ? (
                        <div className="future-order-money-grid">
                          <span><small>{ar ? "المسترد من العربون" : "Deposit refunded"}</small><strong>{formatMoney(item.depositRefundedMinor, item.currency)}</strong></span>
                          <span><small>{ar ? "المسترد من الرصيد" : "Balance refunded"}</small><strong>{formatMoney(item.balanceRefundedMinor, item.currency)}</strong></span>
                          <span><small>{ar ? "العربون المصادَر" : "Deposit forfeited"}</small><strong>{formatMoney(item.depositForfeitedMinor, item.currency)}</strong></span>
                        </div>
                      ) : null}

                      {item.kitchenReleaseState === "Failed" ? (
                        <Alert tone="danger" title={ar ? "تعذر الإرسال التلقائي للتحضير" : "Preparation release failed"}>
                          {item.lastKitchenReleaseError ?? (ar ? "راجع إعدادات KDS ثم أعد المحاولة." : "Review KDS settings, then retry.")}
                        </Alert>
                      ) : null}

                      {active && can("preorders.update") ? (
                        <small className="future-order-edit-policy" data-testid={`preorder-items-edit-window-${item.orderId}`}>
                          {item.status === "Draft"
                            ? ar
                              ? "قبل التأكيد: يمكن إضافة الأصناف أو زيادتها أو تقليلها أو حذفها بحرية."
                              : "Before confirmation: items may be added, increased, reduced or removed freely."
                            : ar
                              ? "بعد التأكيد: الإضافة والزيادة مباشرة، أما التنقيص أو الحذف فيتطلب رمز مدير."
                              : "After confirmation: additions and increases are direct; reductions or removal require a manager PIN."}
                        </small>
                      ) : null}

                      <div className="future-order-actions">
                        <Button
                          data-testid={`open-preorder-items-${item.orderId}`}
                          size="sm"
                          onClick={() => void openDetail(item.orderId)}
                        >
                          {active && can("preorders.update")
                            ? ar ? "فتح الطلب وتعديل الأصناف" : "Open order & edit items"
                            : ar ? "فتح الطلب" : "Open order"}
                        </Button>
                        {can("preorders.update") && active ? (
                          <Button size="sm" variant="secondary" onClick={() => void getPreOrder(item.orderId).then(hydrateEditor).catch((caught) => setError(caught instanceof Error ? caught.message : "Failed"))}>
                            {ar ? "تعديل التفاصيل" : "Edit details"}
                          </Button>
                        ) : null}
                        {can("preorders.confirm") && ["Draft", "AwaitingDeposit"].includes(item.status) && item.itemCount > 0 ? (
                          <Button size="sm" variant="secondary" onClick={() => void confirm(item)}>
                            {ar ? "تأكيد" : "Confirm"}
                          </Button>
                        ) : null}
                        {can("preorders.collect_deposit") && active && !released && item.remainingBalanceMinor > 0 ? (
                          <Button size="sm" variant="secondary" onClick={() => void openDeposit(item)}>
                            {ar ? "تحصيل عربون" : "Collect deposit"}
                          </Button>
                        ) : null}
                        {can("preorders.release") && ["AwaitingDeposit", "Confirmed"].includes(item.status) && !released && item.itemCount > 0 ? (
                          <Button size="sm" variant={item.kitchenReleaseState === "Failed" ? "danger" : "secondary"} onClick={() => void releaseNow(item)}>
                            {item.kitchenReleaseState === "Failed"
                              ? ar ? "إعادة محاولة الإرسال" : "Retry preparation release"
                              : ar ? "إرسال للتحضير الآن (يدوي)" : "Send for preparation now (manual)"}
                          </Button>
                        ) : null}
                        {can("preorders.collect_balance") && active && ["AwaitingDeposit", "Confirmed"].includes(item.status) && item.remainingBalanceMinor > 0 ? (
                          <Button size="sm" onClick={() => void openBalance(item)}>
                            {ar ? "تحصيل الرصيد" : "Collect balance"}
                          </Button>
                        ) : null}
                        {can("preorders.fulfill") && ["AwaitingDeposit", "Confirmed"].includes(item.status) && released && item.remainingBalanceMinor === 0 ? (
                          <Button
                            data-testid={`preorder-handover-${item.orderId}`}
                            size="sm"
                            onClick={() => void openFulfillment(item)}
                          >
                            {ar ? "تسليم وإكمال" : "Handover & complete"}
                          </Button>
                        ) : null}
                        {can("preorders.reschedule") && active && !ready ? (
                          <Button size="sm" variant="secondary" onClick={() => void openReschedule(item)}>
                            {ar ? "إعادة الجدولة" : "Reschedule"}
                          </Button>
                        ) : null}
                        {can("preorders.cancel") && active ? (
                          <Button size="sm" variant="danger" onClick={() => void openCancellation(item)}>
                            {ar ? "إلغاء الحجز" : "Cancel booking"}
                          </Button>
                        ) : null}
                        {can("preorders.print_booking") && item.status !== "Draft" ? (
                          <Button size="sm" variant="secondary" onClick={() => void printBooking(item)}>
                            {ar ? "طباعة الحجز" : "Print booking"}
                          </Button>
                        ) : null}
                      </div>
                    </Stack>
                  </Card>
                );
              })}
            </div>
          ) : loading ? null : (
            <EmptyState icon="orders" title={ar ? "لا توجد طلبات مستقبلية" : "No future orders"} />
          )}
        </Stack>
      </Drawer>

      <Dialog
        open={rescheduleOpen}
        onClose={() => setRescheduleOpen(false)}
        title={ar ? "إعادة جدولة الطلب المستقبلي" : "Reschedule future order"}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => setRescheduleOpen(false)}>{ar ? "إغلاق" : "Close"}</Button>
            <Button onClick={() => void submitReschedule()} disabled={loading || !rescheduleFulfillmentAt || !rescheduleReason.trim()}>
              {ar ? "اعتماد أو إرسال للمدير" : "Apply or request approval"}
            </Button>
          </Inline>
        }
      >
        <Stack>
          {detail ? (
            <Alert tone="info" title={`${detail.summary.bookingNumber} · #${detail.summary.orderNumber}`}>
              {ar
                ? `الموعد الحالي ${new Date(detail.summary.fulfillmentAt).toLocaleString("ar-AE")} — عدد مرات إعادة الجدولة ${detail.summary.rescheduleCount}.`
                : `Current fulfillment ${new Date(detail.summary.fulfillmentAt).toLocaleString("en-AE")} — rescheduled ${detail.summary.rescheduleCount} time(s).`}
            </Alert>
          ) : null}
          <Grid columns={2}>
            <Field label={ar ? "موعد الاستلام الجديد" : "New fulfillment date & time"} type="datetime-local" value={rescheduleFulfillmentAt} onChange={(event) => setRescheduleFulfillmentAt(event.target.value)} />
            <Field label={ar ? "مهلة التحضير بالدقائق" : "Preparation lead minutes"} type="number" min="1" max="1440" value={rescheduleLeadMinutes} onChange={(event) => setRescheduleLeadMinutes(event.target.value)} />
          </Grid>
          <TextAreaField label={ar ? "سبب إعادة الجدولة" : "Reschedule reason"} value={rescheduleReason} onChange={(event) => setRescheduleReason(event.target.value)} />
          <Alert tone="warning" title={ar ? "متى تُطلب موافقة المدير؟" : "When is manager approval required?"}>
            {ar
              ? "عند وجود أي دفعة، أو بعد إرسال الطلب للتحضير، أو داخل مهلة الإغلاق المحددة، أو بعد تجاوز الحد المسموح لمرات إعادة الجدولة."
              : "Approval is required after any captured payment, after preparation release, inside the configured cutoff, or after the reschedule limit is reached."}
          </Alert>
        </Stack>
      </Dialog>

      <Dialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={ar ? "إلغاء الطلب وتسوية العربون" : "Cancel booking & settle deposit"}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>{ar ? "إغلاق" : "Close"}</Button>
            <Button variant="danger" onClick={() => void submitCancellation()} disabled={loading || !cancellationReason.trim()}>
              {ar ? "متابعة الإلغاء" : "Continue cancellation"}
            </Button>
          </Inline>
        }
      >
        <Stack>
          {detail ? (
            <Alert tone="warning" title={`${detail.summary.bookingNumber} · #${detail.summary.orderNumber}`}>
              {ar
                ? `المقبوض حتى الآن ${formatMoney(detail.workflow.order.amountPaidMinor, detail.summary.currency)}. الرصيد المدفوع بعد العربون، إن وجد، يُسترد دائماً؛ وتطبق السياسة المختارة على العربون فقط.`
                : `Captured to date ${formatMoney(detail.workflow.order.amountPaidMinor, detail.summary.currency)}. Any balance settlement is always refunded; the selected policy applies to the deposit only.`}
            </Alert>
          ) : null}
          <SelectField
            label={ar ? "تسوية العربون" : "Deposit settlement"}
            value={cancellationDisposition}
            onChange={(event) => setCancellationDisposition(event.target.value as PreOrderCancellationDisposition)}
          >
            <option value="NoPayment" disabled={(detail?.workflow.order.amountPaidMinor ?? 0) > 0}>{ar ? "لا توجد دفعة" : "No payment"}</option>
            <option value="RefundAll">{ar ? "استرداد العربون كاملاً" : "Refund full deposit"}</option>
            <option value="PartialRefund">{ar ? "استرداد جزئي ومصادرة الباقي" : "Partial refund; forfeit remainder"}</option>
            <option value="ForfeitAll">{ar ? "مصادرة العربون كاملاً" : "Forfeit full deposit"}</option>
          </SelectField>
          {cancellationDisposition === "PartialRefund" ? (
            <Field label={ar ? "مبلغ العربون المسترد" : "Deposit refund amount"} inputMode="decimal" value={partialDepositRefund} onChange={(event) => setPartialDepositRefund(event.target.value)} />
          ) : null}
          <TextAreaField label={ar ? "سبب الإلغاء" : "Cancellation reason"} value={cancellationReason} onChange={(event) => setCancellationReason(event.target.value)} />
          <Alert tone="danger" title={ar ? "شروط إتمام الإلغاء" : "Cancellation conditions"}>
            {ar
              ? "يمكن إلغاء الحجز دون موافقة إذا لم توجد دفعات ولم يُرسل للتحضير وكان خارج مهلة الإلغاء. فيما عدا ذلك، يلزم طلب موافقة وفق سياسة مفعّلة. يبقى الحجز دون تغيير حتى الاعتماد والتنفيذ، وتتطلب التسوية المالية وردية مفتوحة."
              : "An unpaid booking that has not reached preparation and is outside the cancellation cutoff can be cancelled directly. Otherwise, an enabled policy and approval are required. The booking remains unchanged until approval and execution; financial settlement requires an open shift."}
          </Alert>
        </Stack>
      </Dialog>

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={ar ? "تعديل بيانات الحجز" : "Edit booking details"}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => setEditorOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button>
            <Button onClick={() => void save()} disabled={loading || customerBusy || !fulfillmentAt}>{ar ? "حفظ" : "Save"}</Button>
          </Inline>
        }
      >
        <Stack>
          {error ? <Alert tone="danger" title={ar ? "تعذر الحفظ" : "Save failed"}>{error}</Alert> : null}
          {editorDetailsOnly ? (
            <Alert tone="info" title={ar ? "تعديل البيانات التشغيلية" : "Operational details only"}>
              {ar
                ? "يمكن تعديل اسم العميل والهاتف والمركبة والعنوان والملاحظات. الموعد والعربون محميان بعد التأكيد أو الدفع؛ استخدم إعادة الجدولة لتغييرهما."
                : "Customer, phone, vehicle, location and notes remain editable. Fulfillment time and required deposit are protected after confirmation or payment; use Reschedule to change them."}
            </Alert>
          ) : null}
          <Grid columns={2}>
            <Field label={ar ? "تاريخ ووقت الاستلام" : "Fulfillment date & time"} type="datetime-local" value={fulfillmentAt} disabled={editorDetailsOnly} onChange={(event) => setFulfillmentAt(event.target.value)} />
            <Field label={ar ? "مهلة التحضير بالدقائق" : "Preparation lead minutes"} type="number" min="1" max="1440" value={preparationLeadMinutes} disabled={editorDetailsOnly} onChange={(event) => setPreparationLeadMinutes(event.target.value)} />
            <Field
              data-testid="preorder-optional-deposit-target"
              label={ar ? "مبلغ العربون الاختياري" : "Optional deposit target"}
              inputMode="decimal"
              value={depositRequired}
              disabled={editorDetailsOnly}
              onChange={(event) => setDepositRequired(event.target.value)}
            />
            {!editorDetailsOnly ? (
              <Inline>
                <Button
                  data-testid="preorder-no-deposit"
                  size="sm"
                  variant="secondary"
                  onClick={() => setDepositRequired("0")}
                >
                  {ar ? "بدون عربون (0)" : "No deposit (0)"}
                </Button>
                <small>
                  {ar
                    ? "العربون اختياري بالكامل؛ أدخل أي مبلغ ثابت أو صفراً."
                    : "The deposit is fully optional; enter any fixed amount or zero."}
                </small>
              </Inline>
            ) : null}
            {can("customers.read") ? <PreOrderCustomerPicker language={language} customer={linkedCustomer}
              canCreate={can("customers.manage")} disabled={loading || detail?.summary.depositPaidMinor !== 0 || Boolean(detail?.workflow.appliedCoupon)} onBusyChange={setCustomerBusy}
              onSelect={customer => { setLinkedCustomer(customer); setContactName(customer?.name ?? ""); setContactPhone(customer?.phone ?? ""); }} /> : null}
            <Field label={ar ? "اسم العميل" : "Customer name"} value={contactName} onChange={(event) => setContactName(event.target.value)} />
            <Field label={ar ? "رقم الهاتف" : "Phone"} value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} />
            <Field label={ar ? "رقم المركبة" : "Vehicle number"} value={vehicleNumber} onChange={(event) => setVehicleNumber(event.target.value)} />
          </Grid>
          <TextAreaField
            data-testid="preorder-address-notes"
            label={ar ? "تفاصيل العنوان / الموقع" : "Address / location details"}
            placeholder={ar ? "فيلا، مزرعة، شارع، بوابة أو تعليمات الوصول" : "Villa, farm, street, gate or access instructions"}
            value={addressNotes}
            onChange={(event) => setAddressNotes(event.target.value)}
          />
          {detail?.summary.customerAddress ? (
            <Alert tone="info" title={ar ? "العنوان المحفوظ في ملف العميل" : "Saved customer address"}>
              {detail.summary.customerAddress}
            </Alert>
          ) : null}
          <TextAreaField label={ar ? "ملاحظات الحجز" : "Booking notes"} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Stack>
      </Dialog>

      <Dialog
        open={depositOpen}
        onClose={() => setDepositOpen(false)}
        title={ar ? "تحصيل عربون الطلب المستقبلي" : "Collect future-order deposit"}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => setDepositOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button>
            <Button onClick={() => void collectDeposit()} disabled={loading || !depositAmount}>{ar ? "تحصيل العربون" : "Capture deposit"}</Button>
          </Inline>
        }
      >
        <Stack>
          {detail ? (
            <Alert tone="info" title={`${detail.summary.bookingNumber} · #${detail.summary.orderNumber}`}>
              {ar
                ? `الإجمالي ${formatMoney(detail.summary.orderTotalMinor, detail.summary.currency)} — المتبقي ${formatMoney(detail.summary.remainingBalanceMinor, detail.summary.currency)}`
                : `Total ${formatMoney(detail.summary.orderTotalMinor, detail.summary.currency)} — remaining ${formatMoney(detail.summary.remainingBalanceMinor, detail.summary.currency)}`}
            </Alert>
          ) : null}
          <SelectField label={ar ? "طريقة الدفع" : "Payment method"} value={depositMethod} onChange={(event) => setDepositMethod(event.target.value as PaymentMethod)}>
            <option value="Cash">{ar ? "نقدي" : "Cash"}</option>
            <option value="Card">{ar ? "بطاقة / شبكة" : "Card / Terminal"}</option>
          </SelectField>
          <Grid columns={2}>
            <Field label={ar ? "مبلغ العربون" : "Deposit amount"} inputMode="decimal" value={depositAmount} onChange={(event) => { setDepositAmount(event.target.value); if (depositMethod !== "Cash") setDepositTendered(event.target.value); }} />
            {depositMethod === "Cash" ? (
              <Field label={ar ? "المبلغ المستلم" : "Tendered cash"} inputMode="decimal" value={depositTendered} onChange={(event) => setDepositTendered(event.target.value)} />
            ) : (
              <Field label={ar ? "مرجع العملية" : "Terminal reference"} value={depositReference} onChange={(event) => setDepositReference(event.target.value)} />
            )}
          </Grid>
        </Stack>
      </Dialog>

      <Dialog
        open={balanceOpen}
        onClose={() => setBalanceOpen(false)}
        title={ar ? "تحصيل الرصيد المتبقي" : "Collect remaining balance"}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => setBalanceOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button>
            <Button onClick={() => void collectBalance()} disabled={loading || !balanceAmount}>{ar ? "تحصيل الرصيد" : "Capture balance"}</Button>
          </Inline>
        }
      >
        <Stack>
          {detail ? (
            <Alert tone="info" title={`${detail.summary.bookingNumber} · ${ar ? "دفع الرصيد المتبقي" : "Remaining balance payment"}`}>
              {ar
                ? `الرصيد المطلوب ${formatMoney(detail.summary.remainingBalanceMinor, detail.summary.currency)}. يمكن تحصيله قبل الجاهزية، وسيُسجل في الوردية المفتوحة الحالية دون إكمال الطلب أو تغيير حالة التحضير.`
                : `Balance due ${formatMoney(detail.summary.remainingBalanceMinor, detail.summary.currency)}. It may be collected before Ready and is recorded in the current open shift without completing the order or changing preparation state.`}
            </Alert>
          ) : null}
          <SelectField label={ar ? "طريقة الدفع" : "Payment method"} value={balanceMethod} onChange={(event) => setBalanceMethod(event.target.value as PaymentMethod)}>
            <option value="Cash">{ar ? "نقدي" : "Cash"}</option>
            <option value="Card">{ar ? "بطاقة / شبكة" : "Card / Terminal"}</option>
          </SelectField>
          <Grid columns={2}>
            <Field label={ar ? "مبلغ الرصيد" : "Balance amount"} inputMode="decimal" value={balanceAmount} onChange={(event) => { setBalanceAmount(event.target.value); if (balanceMethod !== "Cash") setBalanceTendered(event.target.value); }} />
            {balanceMethod === "Cash" ? (
              <Field label={ar ? "المبلغ المستلم" : "Tendered cash"} inputMode="decimal" value={balanceTendered} onChange={(event) => setBalanceTendered(event.target.value)} />
            ) : (
              <Field label={ar ? "مرجع العملية" : "Terminal reference"} value={balanceReference} onChange={(event) => setBalanceReference(event.target.value)} />
            )}
          </Grid>
        </Stack>
      </Dialog>

      <Dialog
        open={fulfillOpen}
        onClose={() => setFulfillOpen(false)}
        title={ar ? "تسليم الطلب وإكماله" : "Handover and complete future order"}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => setFulfillOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button>
            <Button onClick={() => void completeFulfillment()} disabled={loading}>{ar ? "تأكيد التسليم وإصدار الإيصال" : "Confirm handover & issue receipt"}</Button>
          </Inline>
        }
      >
        <Stack>
          {detail ? (
            <Alert
              tone={detail.summary.orderStatus === "Ready" && detail.summary.fulfillmentStatus === "Ready" ? "info" : "warning"}
              title={detail.summary.orderStatus === "Ready" && detail.summary.fulfillmentStatus === "Ready"
                ? `${detail.summary.bookingNumber} · #${detail.summary.orderNumber}`
                : ar ? "المطبخ لم يضع الطلب «جاهز»" : "Kitchen has not marked this order Ready"}
            >
              {detail.summary.orderStatus === "Ready" && detail.summary.fulfillmentStatus === "Ready"
                ? ar
                  ? `سيُغلق الطلب نهائياً، ويُربط بالوردية الحالية، ويصدر الإيصال RCP-${detail.summary.orderNumber}.`
                  : `This will complete the order, assign it to the current shift and issue RCP-${detail.summary.orderNumber}.`
                : ar
                  ? `الطلب مُرسل للمطبخ ومدفوع بالكامل، لكن KDS لم يسجل «جاهز». تأكيد التسليم يعني أن الكاشير استلم الطلب فعلياً من المطبخ؛ سيعتمد Leylion الجاهزية وقت التسليم، يكمل الطلب ويصدر الإيصال RCP-${detail.summary.orderNumber}.`
                  : `The order is released to the kitchen and fully paid, but KDS has not marked it Ready. Confirming handover means the cashier has physically received it from the kitchen; Leylion will record readiness at handover, complete the order and issue RCP-${detail.summary.orderNumber}.`}
            </Alert>
          ) : null}
          <TextAreaField
            label={ar ? "ملاحظة التسليم (اختياري)" : "Handover note (optional)"}
            value={handoverNote}
            onChange={(event) => setHandoverNote(event.target.value)}
          />
        </Stack>
      </Dialog>
    </>
  );
}

function renderBookingReceipt(receipt: BookingReceipt, ar: boolean): string {
  const money = (minor: number) =>
    formatMoneyMinor(minor, receipt.currency, ar ? "ar-AE" : "en-AE");
  const rows = receipt.lines.map((line) => `
    <tr>
      <td>${escapeHtml(ar ? line.productNameAr : line.productNameEn)}<small>${escapeHtml(ar ? line.variantNameAr : line.variantNameEn)}</small></td>
      <td>${line.quantityMilli / 1000}</td>
      <td>${escapeHtml(money(line.totalMinor))}</td>
    </tr>`).join("");
  const payments = receipt.depositPayments.map((payment) => `
    <div class="row"><span>${escapeHtml(valueLabel(payment.method, ar ? "ar" : "en"))}</span><strong>${escapeHtml(money(payment.amountMinor))}</strong></div>`).join("");
  return `<!doctype html>
<html lang="${ar ? "ar" : "en"}" dir="${ar ? "rtl" : "ltr"}">
<head><meta charset="utf-8"><title>${escapeHtml(receipt.bookingNumber)}</title>
<style>
@page{size:80mm auto;margin:4mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#111;margin:0;font-size:12px}h1{font-size:18px;margin:0;text-align:center}h2{font-size:14px;margin:5px 0;text-align:center}.muted{color:#555}.center{text-align:center}.divider{border-top:1px dashed #777;margin:10px 0}.row{display:flex;justify-content:space-between;gap:10px;margin:5px 0}.row.location strong{max-width:62%;white-space:pre-wrap;text-align:${ar ? "left" : "right"}}.box{border:1px solid #222;border-radius:7px;padding:8px;margin:8px 0}table{width:100%;border-collapse:collapse}th,td{padding:5px 2px;border-bottom:1px solid #ddd;text-align:${ar ? "right" : "left"}}td:last-child,th:last-child{text-align:${ar ? "left" : "right"}}small{display:block;color:#666;margin-top:2px}.total{font-size:15px}.balance{font-size:16px;border:2px solid #111}.footer{text-align:center;margin-top:12px;font-size:10px}</style></head>
<body>
<h1>${escapeHtml(receipt.businessName || BRAND.productName)}</h1><div class="center muted">${escapeHtml(receipt.branchName)}${receipt.branchAddress ? `<br>${escapeHtml(receipt.branchAddress)}` : ""}</div>
<div class="divider"></div><h2>${ar ? "تأكيد حجز / طلب مسبق" : "Booking Confirmation / Pre-order"}</h2>
<div class="row"><span>${ar ? "رقم الحجز" : "Booking"}</span><strong>${escapeHtml(receipt.bookingNumber)}</strong></div>
<div class="row"><span>${ar ? "رقم الطلب" : "Order"}</span><strong>#${escapeHtml(receipt.orderNumber)}</strong></div>
<div class="row"><span>${ar ? "موعد الاستلام" : "Fulfillment"}</span><strong>${escapeHtml(new Date(receipt.fulfillmentAt).toLocaleString(ar ? "ar-AE" : "en-AE"))}</strong></div>
<div class="box"><div class="row"><span>${ar ? "اسم العميل" : "Customer"}</span><strong>${escapeHtml(receipt.customerName || "—")}</strong></div><div class="row"><span>${ar ? "الهاتف" : "Phone"}</span><strong>${escapeHtml(receipt.customerPhone || "—")}</strong></div>${receipt.locationDetails ? `<div class="row location"><span>${ar ? "العنوان / الموقع" : "Address / location"}</span><strong>${escapeHtml(receipt.locationDetails)}</strong></div>` : ""}<div class="row"><span>${ar ? "نوع الخدمة" : "Service"}</span><strong>${escapeHtml(receipt.serviceType)}</strong></div></div>
<table><thead><tr><th>${ar ? "الصنف" : "Item"}</th><th>${ar ? "الكمية" : "Qty"}</th><th>${ar ? "الإجمالي" : "Total"}</th></tr></thead><tbody>${rows}</tbody></table>
<div class="divider"></div><div class="row"><span>${ar ? "إجمالي الطلب" : "Order total"}</span><strong>${escapeHtml(money(receipt.totalMinor))}</strong></div>
<div class="box">${payments || `<div class="muted">${ar ? "لم يُسجل عربون بعد" : "No deposit captured yet"}</div>`}<div class="row total"><span>${ar ? "إجمالي العربون المدفوع" : "Deposit paid"}</span><strong>${escapeHtml(money(receipt.depositPaidMinor))}</strong></div></div>
<div class="box balance"><div class="row"><span>${ar ? "الرصيد المتبقي عند الاستلام" : "Remaining at fulfillment"}</span><strong>${escapeHtml(money(receipt.balanceDueMinor))}</strong></div></div>
${receipt.notes ? `<div class="box"><strong>${ar ? "ملاحظات" : "Notes"}</strong><br>${escapeHtml(receipt.notes)}</div>` : ""}
<div class="footer">${ar ? "هذا المستند يؤكد الحجز ولا يثبت سداد كامل قيمة الطلب." : "This document confirms the booking and does not prove full settlement."}<br>${escapeHtml(new Date(receipt.issuedAt).toLocaleString(ar ? "ar-AE" : "en-AE"))}</div>
</body></html>`;
}
